/**
 * Background poller that flips PPPC activity entries from "deployed" to
 * "installed" without requiring the device detail page to be open.
 *
 * Until the smplify backend offers a push channel for MDM install
 * confirmations, we approximate real-time updates by:
 *
 *   1. Tracking every (policyId, deviceId) pair we've ever recorded a
 *      "deployed" or "installed" event for (sourced from the existing
 *      PPPC activity log in localStorage).
 *   2. Polling `GET /api/v1/devices/:id/policies` for each unique
 *      device on a configurable interval, looking for our policies in
 *      the response with a status that indicates installation.
 *   3. Recording an "installed" entry via `recordPppcActivityIfNew`,
 *      which dispatches `pppc-activity-updated` — the timeline and
 *      global Logs page already subscribe to that event, so the UI
 *      refreshes the moment the install lands.
 *
 * Once a (policyId, deviceId) pair is confirmed installed, it is dropped
 * from the polling set so we don't keep hammering the API for
 * already-resolved deployments. The poller pauses while the tab is
 * hidden and resumes on `visibilitychange` to keep network usage low.
 *
 * The DeviceConfigurations page still records install events on its
 * own when an admin opens a device — this poller is purely additive and
 * its dedup is handled by `recordPppcActivityIfNew`'s natural keying
 * on (policyId, deviceId, action).
 */

import * as devicesApi from "@/services/devicesApi";
import { getCurrentUserEmail } from "@/lib/auth";
import {
  getAllPppcActivity,
  recordPppcActivityIfNew,
  type PppcActivityEntry,
} from "@/services/pppcActivityLog";

const DEFAULT_INTERVAL_MS = 30_000; // 30s — friendly to the backend
const MIN_INTERVAL_MS = 5_000;

const INSTALLED_STATUSES = new Set([
  "installed",
  "active",
  "applied",
  "success",
]);

// Resilient field extractors — the backend has shipped multiple casings
// for the policy DTO. Mirror what DeviceConfigurations does.
const getId = (p: any): string | undefined =>
  p?.id || p?.policyId || p?.uuid;
const getName = (p: any): string =>
  p?.name || p?.policyName || p?.displayName || p?.title || "Policy";
const getStatus = (p: any): string | undefined =>
  p?.status || p?.installStatus || p?.state;
const getDate = (p: any): string | undefined =>
  p?.installedAt || p?.installDate || p?.createdAt || p?.updatedAt;

type DevicePoliciesFetcher = (deviceId: string) => Promise<unknown[]>;

interface PendingTarget {
  policyId: string;
  deviceId: string;
  /** Surface fields cached from the original deploy entry so we can
   *  build a useful summary even if the device fetch returns a sparse DTO. */
  appName?: string;
  appId?: string;
  bundleId?: string;
  tagIds?: string[];
}

/**
 * Build the set of (policyId, deviceId) pairs that still need to be
 * confirmed as installed. A pair is included iff:
 *   - we have at least one "deployed" entry for it, AND
 *   - we have NOT yet recorded any "installed" entry for it.
 */
export function computePendingTargets(
  entries: PppcActivityEntry[] = getAllPppcActivity(),
): PendingTarget[] {
  const installed = new Set<string>();
  for (const e of entries) {
    if (e.action === "installed" && e.deviceId) {
      installed.add(`${e.policyId}::${e.deviceId}`);
    }
  }

  const seen = new Set<string>();
  const out: PendingTarget[] = [];
  for (const e of entries) {
    if (e.action !== "deployed" || !e.deviceId) continue;
    const key = `${e.policyId}::${e.deviceId}`;
    if (installed.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push({
      policyId: e.policyId,
      deviceId: e.deviceId,
      appName: e.snapshot?.appName,
      appId: e.appId,
      bundleId: e.bundleId,
      tagIds: e.tagIds,
    });
  }
  return out;
}

interface PollerOptions {
  /** Polling interval in ms. Clamped to MIN_INTERVAL_MS. */
  intervalMs?: number;
  /** Override the device-policies fetcher (used in tests). */
  fetchDevicePolicies?: DevicePoliciesFetcher;
  /** Override `document.visibilityState` checks (used in tests). */
  isVisible?: () => boolean;
}

/**
 * Poll the backend once and emit "installed" entries for any pending
 * targets that have been confirmed. Returns the number of new entries
 * recorded — useful for tests.
 */
export async function pollOnce(opts: PollerOptions = {}): Promise<number> {
  const fetchDevicePolicies: DevicePoliciesFetcher =
    opts.fetchDevicePolicies ??
    (async (deviceId) => {
      const res = await devicesApi.getDevicePolicies(deviceId);
      return Array.isArray(res) ? res : [];
    });

  const targets = computePendingTargets();
  if (targets.length === 0) return 0;

  // Group targets by deviceId so we make one request per device.
  const byDevice = new Map<string, PendingTarget[]>();
  for (const t of targets) {
    const list = byDevice.get(t.deviceId) ?? [];
    list.push(t);
    byDevice.set(t.deviceId, list);
  }

  let recorded = 0;
  await Promise.all(
    Array.from(byDevice.entries()).map(async ([deviceId, group]) => {
      let policies: unknown[];
      try {
        policies = await fetchDevicePolicies(deviceId);
      } catch {
        // Network/CORS hiccups are non-fatal — try again next tick.
        return;
      }
      if (!Array.isArray(policies) || policies.length === 0) return;

      // Index returned policies by id for O(1) lookup per target.
      const byId = new Map<string, any>();
      for (const p of policies) {
        const id = getId(p);
        if (id) byId.set(id, p);
      }

      for (const target of group) {
        const p = byId.get(target.policyId);
        if (!p) continue;
        const status = getStatus(p);
        if (!status || !INSTALLED_STATUSES.has(status.toLowerCase())) continue;

        const result = recordPppcActivityIfNew({
          policyId: target.policyId,
          deviceId,
          appId: target.appId,
          bundleId: target.bundleId,
          tagIds: target.tagIds,
          action: "installed",
          actor: getCurrentUserEmail(),
          summary: `${getName(p) || target.appName || "PPPC policy"} confirmed installed on device`,
          details: { status, source: "pppcInstallPoller" },
          at: getDate(p) ?? undefined,
        });
        if (result) recorded += 1;
      }
    }),
  );
  return recorded;
}

interface RunningPoller {
  stop(): void;
  /** Poll immediately, outside the timer schedule. */
  pokeNow(): Promise<number>;
}

/**
 * Start a long-running poller. Safe to call multiple times — each call
 * returns its own controller, but in practice the app mounts a single
 * instance via `PppcInstallPoller`.
 */
export function startPppcInstallPoller(opts: PollerOptions = {}): RunningPoller {
  const interval = Math.max(MIN_INTERVAL_MS, opts.intervalMs ?? DEFAULT_INTERVAL_MS);
  const isVisible =
    opts.isVisible ??
    (() => (typeof document === "undefined" ? true : document.visibilityState !== "hidden"));

  let stopped = false;
  let inflight: Promise<number> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (stopped) return;
    if (!isVisible()) {
      schedule();
      return;
    }
    if (inflight) {
      // Coalesce overlapping ticks.
      schedule();
      return;
    }
    inflight = pollOnce(opts).finally(() => {
      inflight = null;
    });
    try {
      await inflight;
    } catch {
      /* swallowed — pollOnce is already defensive */
    }
    schedule();
  };

  const schedule = () => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(tick, interval);
  };

  // Re-poll promptly when the tab becomes visible again, and whenever a
  // new "deployed" event is recorded (so the user doesn't wait a full
  // interval for the first install confirmation after a fresh deploy).
  const onVisibility = () => {
    if (isVisible()) void tick();
  };
  const onActivity = () => {
    void tick();
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("pppc-activity-updated", onActivity);
  }

  // Kick off an initial pass on the next tick so callers can wire up
  // listeners synchronously before the first network request fires.
  schedule();

  return {
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pppc-activity-updated", onActivity);
      }
    },
    pokeNow: () => pollOnce(opts),
  };
}
